/**
 * Transaction Tracker - 追踪所有 operation 操作
 * 使用 IndexDB 存储，用于调试递归删除问题
 */

import localforage from 'localforage';

const DB_KEY = 'flowus.transaction_tracker_logs';
const MAX_LOGS = 5000; // 最多保存日志数量
const MAX_DAYS = 7; // 最多保存天数

interface OperationLog {
  /** 操作 ID */
  id: string;
  /** spaceId */
  spaceId: string;
  /** operation 内容 */
  operation: any;
  /** Redux action 类型 */
  actionType?: string;
  /** 调用栈（过滤后的业务代码部分） */
  stack?: string;
  /** 时间戳 */
  timestamp: number;
  /** 额外上下文 */
  context?: Record<string, any>;
}

class TransactionTracker {
  private enabled = true;
  private db: typeof localforage;

  constructor() {
    this.db = localforage.createInstance({
      name: 'flowus-tracker',
      storeName: 'operation-logs',
    });
  }

  /**
   * 启用追踪
   */
  enable() {
    this.enabled = true;
  }

  /**
   * 禁用追踪
   */
  disable() {
    this.enabled = false;
  }

  /**
   * 捕获并过滤调用栈，只保留业务代码部分
   */
  private captureStack(): string {
    try {
      const stack = new Error().stack || '';
      const lines = stack.split('\n');

      // 过滤掉：
      // 1. Error 自身
      // 2. TransactionTracker 内部方法
      // 3. persistence.ts 中的 addOperation (middleware 层)
      // 4. Redux 内部代码
      const filtered = lines.filter((line) => {
        // 跳过 Error 本身和 TransactionTracker 内部
        if (
          line.includes('Error') ||
          line.includes('TransactionTracker') ||
          line.includes('transaction-tracker')
        ) {
          return false;
        }

        // 跳过 Redux 和 middleware 内部
        if (
          line.includes('node_modules/redux') ||
          line.includes('node_modules/@reduxjs') ||
          (line.includes('persistence.ts') && line.includes('addOperation'))
        ) {
          return false;
        }

        return true;
      });

      // 只保留前 10 层，避免太长
      return filtered.slice(0, 10).join('\n');
    } catch {
      return '';
    }
  }

  /**
   * 过滤掉超过指定天数的日志
   */
  private filterOldLogs(logs: OperationLog[], maxDays: number = MAX_DAYS): OperationLog[] {
    const cutoffTime = Date.now() - maxDays * 24 * 60 * 60 * 1000;
    return logs.filter((log) => log.timestamp >= cutoffTime);
  }

  /**
   * 记录一个 operation
   */
  async track(spaceId: string, operation: any, context?: Record<string, any>) {
    if (!this.enabled) return;

    try {
      const log: OperationLog = {
        id: `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        spaceId,
        operation,
        actionType: context?.actionType,
        stack: this.captureStack(), // 捕获调用栈
        timestamp: Date.now(),
        context,
      };

      // 获取现有日志
      let logs = await this.getLogs();

      // 先过滤掉超过 MAX_DAYS 天的日志
      logs = this.filterOldLogs(logs);

      // 添加新日志
      logs.push(log);

      // 限制最多保存 MAX_LOGS 条（防止占用太多空间）
      if (logs.length > MAX_LOGS) {
        logs.splice(0, logs.length - MAX_LOGS);
      }

      // 保存到 IndexDB
      await this.db.setItem(DB_KEY, logs);
    } catch (error) {
      // 静默失败，不影响主流程
    }
  }

  /**
   * 判断是否是删除操作
   */
  private isDeleteOperation(operation: any): boolean {
    return operation.command === 'listRemove' || operation.table === 'space';
  }

  /**
   * 获取所有日志（自动过滤超过 MAX_DAYS 天的日志）
   */
  async getLogs(): Promise<OperationLog[]> {
    try {
      const logs = await this.db.getItem<OperationLog[]>(DB_KEY);
      if (!logs) return [];

      // 自动过滤超过 MAX_DAYS 天的日志
      return this.filterOldLogs(logs);
    } catch (error) {
      return [];
    }
  }

  /**
   * 获取最近N条日志
   */
  async getRecentLogs(count = 50): Promise<OperationLog[]> {
    const logs = await this.getLogs();
    return logs.slice(-count);
  }

  /**
   * 按 spaceId 过滤日志
   */
  async getLogsBySpaceId(spaceId: string): Promise<OperationLog[]> {
    const logs = await this.getLogs();
    return logs.filter((log) => log.spaceId === spaceId);
  }

  /**
   * 按时间范围过滤日志
   */
  async getLogsByTimeRange(startTime: number, endTime: number): Promise<OperationLog[]> {
    const logs = await this.getLogs();
    return logs.filter((log) => log.timestamp >= startTime && log.timestamp <= endTime);
  }

  /**
   * 清空所有日志
   */
  async clear() {
    try {
      await this.db.setItem(DB_KEY, []);
    } catch (error) {
      // 静默失败
    }
  }

  /**
   * 手动清理超过指定天数的旧日志
   */
  async cleanupOldLogs(days: number = MAX_DAYS) {
    try {
      const logs = await this.db.getItem<OperationLog[]>(DB_KEY);
      if (!logs) return;

      const filtered = this.filterOldLogs(logs, days);
      await this.db.setItem(DB_KEY, filtered);

      return {
        before: logs.length,
        after: filtered.length,
        removed: logs.length - filtered.length,
      };
    } catch (error) {
      return { before: 0, after: 0, removed: 0 };
    }
  }

  /**
   * 导出日志为 JSON
   */
  async exportToJSON(): Promise<string> {
    const logs = await this.getLogs();
    return JSON.stringify(
      {
        exportTime: new Date().toISOString(),
        totalCount: logs.length,
        logs,
      },
      null,
      2
    );
  }

  /**
   * 导出日志并下载为文件
   */
  async downloadLogs() {
    try {
      const json = await this.exportToJSON();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transaction-logs-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      // 静默失败
    }
  }

  /**
   * 生成删除操作分析报告
   */
  async generateDeleteReport(): Promise<string> {
    const logs = await this.getLogs();
    const deleteLogs = logs.filter((log) => this.isDeleteOperation(log.operation));

    if (deleteLogs.length === 0) {
      return '没有发现删除操作';
    }

    const firstLog = deleteLogs[0]!;
    const lastLog = deleteLogs[deleteLogs.length - 1]!;

    let report = `删除操作分析报告\n`;
    report += `===================\n`;
    report += `总删除操作数: ${deleteLogs.length}\n`;
    report += `时间范围: ${new Date(firstLog.timestamp).toISOString()} ~ ${new Date(
      lastLog.timestamp
    ).toISOString()}\n\n`;

    // 按 command 分组统计
    const commandCount = new Map<string, number>();
    deleteLogs.forEach((log) => {
      const cmd = log.operation.command;
      commandCount.set(cmd, (commandCount.get(cmd) || 0) + 1);
    });

    report += `操作类型统计:\n`;
    commandCount.forEach((count, cmd) => {
      report += `  ${cmd}: ${count}\n`;
    });

    // 按 spaceId 分组统计
    const spaceCount = new Map<string, number>();
    deleteLogs.forEach((log) => {
      spaceCount.set(log.spaceId, (spaceCount.get(log.spaceId) || 0) + 1);
    });

    report += `\nSpace 统计:\n`;
    spaceCount.forEach((count, spaceId) => {
      report += `  ${spaceId}: ${count}\n`;
    });

    report += `\n最近20条删除操作:\n`;
    deleteLogs.slice(-20).forEach((log, index) => {
      report += `\n[${index + 1}] ${log.actionType || 'Unknown'}\n`;
      report += `  SpaceId: ${log.spaceId}\n`;
      report += `  Command: ${log.operation.command}\n`;
      report += `  Table: ${log.operation.table}\n`;
      report += `  Target ID: ${log.operation.id}\n`;
      report += `  时间: ${new Date(log.timestamp).toISOString()}\n`;
      if (log.stack) {
        report += `  调用栈:\n${log.stack
          .split('\n')
          .map((line) => `    ${line}`)
          .join('\n')}\n`;
      }
      if (log.context) {
        report += `  上下文: ${JSON.stringify(log.context)}\n`;
      }
    });

    return report;
  }

  /**
   * 获取统计信息
   */
  async getStats() {
    const logs = await this.getLogs();
    const deleteLogs = logs.filter((log) => this.isDeleteOperation(log.operation));

    const oldestLog = logs[0];
    const newestLog = logs[logs.length - 1];

    return {
      totalLogs: logs.length,
      deleteLogs: deleteLogs.length,
      enabled: this.enabled,
      oldestLog: oldestLog ? new Date(oldestLog.timestamp).toISOString() : null,
      newestLog: newestLog ? new Date(newestLog.timestamp).toISOString() : null,
    };
  }

  /**
   * 格式化单条日志用于 console 输出
   */
  formatLog(log: OperationLog): string {
    let output = `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    output += `🔍 Operation Log [${log.id}]\n`;
    output += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    output += `⏰ 时间: ${new Date(log.timestamp).toLocaleString('zh-CN')}\n`;
    output += `📦 SpaceId: ${log.spaceId}\n`;
    output += `🎯 Action: ${log.actionType || 'Unknown'}\n`;
    output += `📋 Operation:\n`;
    output += `   Command: ${log.operation.command}\n`;
    output += `   Table: ${log.operation.table}\n`;
    output += `   ID: ${log.operation.id}\n`;
    output += `   Path: ${JSON.stringify(log.operation.path)}\n`;
    output += `   Args: ${JSON.stringify(log.operation.args, null, 2).split('\n').join('\n   ')}\n`;

    if (log.stack) {
      output += `\n📍 调用栈:\n`;
      output += log.stack
        .split('\n')
        .map((line) => `   ${line}`)
        .join('\n');
      output += `\n`;
    }

    if (log.context) {
      output += `\n📝 上下文: ${JSON.stringify(log.context, null, 2)}\n`;
    }

    output += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    return output;
  }

  /**
   * 查看最近的日志（格式化输出到 console）
   */
  async viewRecentLogs(count = 10) {
    const logs = await this.getRecentLogs(count);
    logs.forEach((log) => {
      console.log(this.formatLog(log));
    });
    return logs;
  }
}

// 创建全局单例
export const transactionTracker = new TransactionTracker();

// 挂载到 window 对象上，方便在控制台调试
if (typeof window !== 'undefined') {
  (window as any).__transactionTracker = {
    // 获取日志
    getLogs: () => transactionTracker.getLogs(),
    getRecentLogs: (count?: number) => transactionTracker.getRecentLogs(count),
    getLogsBySpaceId: (spaceId: string) => transactionTracker.getLogsBySpaceId(spaceId),

    // 查看日志（格式化输出）
    viewRecentLogs: (count?: number) => transactionTracker.viewRecentLogs(count),

    // 生成报告
    generateDeleteReport: () => transactionTracker.generateDeleteReport(),
    getStats: () => transactionTracker.getStats(),

    // 导出
    exportToJSON: () => transactionTracker.exportToJSON(),
    downloadLogs: () => transactionTracker.downloadLogs(),

    // 管理
    clear: () => transactionTracker.clear(),
    cleanupOldLogs: (days?: number) => transactionTracker.cleanupOldLogs(days),
    enable: () => transactionTracker.enable(),
    disable: () => transactionTracker.disable(),
  };
}
